import { Fragment, useEffect, useMemo, useState } from 'react';
import { X, Tag, CheckCircle2, Printer } from 'lucide-react';
import { useCurrency } from '@kss/currency';
import { useToast, Button, computeCharges, gstBuckets, onlyCode } from '@kss/ui';
import { useConfig } from '@kss/theme';
import {
  billService,
  apiMessage,
  couponService,
  settingsService,
  paymentService,
  type ApiCustomerBill
} from '@kss/api';

export interface BillLine {
  name: string;
  qty: number;
  price: number;
  /** This line's GST rate %, resolved server-side from the menu item. Undefined = outlet rate. */
  gst?: number;
  /** Whether `price` already contains the GST. Undefined = the outlet's setting. */
  taxInclusive?: boolean;
}

type Tax = { cgst: number; sgst: number; service: number; inclusive: boolean };

const pct = (s?: string) => {
  const n = parseFloat((s ?? '').replace('%', '').trim());
  return Number.isFinite(n) ? n : 0;
};

/** A GST rate for a label, e.g. 5 → "5", 2.5 → "2.5". */
const fmtRate = (r: number) => (Number.isInteger(r) ? String(r) : r.toFixed(1));

/** Load the Cashfree v3 JS SDK once and return a checkout instance for the mode. */
async function loadCashfree(mode: string): Promise<any> {
  const w = window as unknown as { Cashfree?: (o: { mode: string }) => unknown };
  if (!w.Cashfree) {
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Cashfree SDK failed to load'));
      document.head.appendChild(s);
    });
  }
  if (!w.Cashfree) throw new Error('Cashfree SDK unavailable');
  return w.Cashfree({ mode: mode === 'PRODUCTION' ? 'production' : 'sandbox' });
}

/**
 * The customer "View Bill" popup: itemised bill + coupon + full tax breakdown, then the
 * request → approve → Cashfree-pay lifecycle. Pay Now stays hidden until a waiter/admin
 * approves the amount; once paid, the receipt can be printed / downloaded.
 */
export default function BillModal({
  open,
  onClose,
  items,
  phone,
  tableCode,
  bill,
  onChanged
}: {
  open: boolean;
  onClose: () => void;
  items: BillLine[];
  phone?: string;
  tableCode: string;
  bill: ApiCustomerBill | null;
  onChanged: () => void;
}) {
  const { format } = useCurrency();
  const toast = useToast();
  // The printed bill used to hardcode "The Backyard" as its heading and claim every bill
  // was "Paid via Cashfree" regardless of how it was actually settled.
  const { brand } = useConfig();
  const [tax, setTax] = useState<Tax>({ cgst: 0, sgst: 0, service: 0, inclusive: false });
  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);

  const requested = bill != null && bill.status !== 'paid';
  const approved = bill?.status === 'approved';
  const locked = requested; // amount is fixed once the bill is requested

  useEffect(() => {
    if (!open) return;
    settingsService
      .getOutlet()
      .then((o) => setTax({ cgst: pct(o.cgst), sgst: pct(o.sgst), service: pct(o.serviceCharge), inclusive: !!o.taxInclusive }))
      .catch(() => {});
  }, [open]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);
  // The outlet's combined GST. NO invented default: when the outlet has configured no rate, the
  // fallback for a line without its own rate is 0% — the bill shows GST only where it's actually set,
  // never a hardcoded 5% (which showed tax on items nobody had configured any GST for).
  const hasSplitGst = tax.cgst + tax.sgst > 0;
  const gstRate = tax.cgst + tax.sgst;
  // "Prices include tax" (Outlet.TaxInclusive): when ON, the menu prices ALREADY contain GST, so we
  // extract it from the subtotal for display and DON'T add it again (total = subtotal). When OFF,
  // GST is added on top (total = subtotal + GST). The base the GST is computed on differs accordingly.
  // Taxed PER LINE at each item's own rate and inclusivity — a bill mixing 5% food with an 18%
  // packaged drink used to charge both at one outlet rate. Lines without their own values fall back
  // to the outlet's, so a single-rate menu behaves exactly as before.
  const charges = useMemo(
    () =>
      computeCharges(
        subtotal,
        discount,
        { rate: gstRate, inclusive: tax.inclusive },
        items.map((i) => ({ amount: i.qty * i.price, gst: i.gst, inclusive: i.taxInclusive }))
      ),
    [subtotal, discount, gstRate, tax.inclusive, items]
  );
  // `charges.inclusive` is true only when NOTHING was added, which is what decides the tax rows.
  const addsTax = !charges.inclusive;
  // Rate-wise GST: one row per distinct rate the items actually carry (5% food, 18% packaged drink),
  // and NOTHING for an item with no GST. A single blended "GST 5%" line mislabelled a mixed bill and
  // showed a rate on items that were never taxed. Only the exclusive buckets add to the total and so
  // appear on screen; the printed tax invoice lists every bucket (inclusive included).
  const buckets = useMemo(
    () => gstBuckets(subtotal, discount, { rate: gstRate, inclusive: tax.inclusive }, items.map((i) => ({ amount: i.qty * i.price, gst: i.gst, inclusive: i.taxInclusive }))),
    [subtotal, discount, gstRate, tax.inclusive, items]
  );
  const shownBuckets = buckets.filter((b) => !b.inclusive); // inclusive tax is already in the price
  const serviceAmt = Math.round((subtotal * tax.service) / 100);
  const total = Math.max(0, charges.total + serviceAmt);
  // Once approved, the amount the customer actually pays is the amount the admin confirmed.
  const payable = approved && bill ? Math.round(bill.approvedAmount || total) : total;

  async function applyCoupon() {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await couponService.validate(code.trim(), subtotal);
      if (r.valid) {
        setDiscount(Math.round(r.discount));
        setCouponMsg(r.message || `Coupon applied · −${format(Math.round(r.discount))}`);
      } else {
        setDiscount(0);
        setCouponMsg(r.message || 'Invalid coupon');
      }
    } catch {
      setCouponMsg('Could not check the coupon');
    } finally {
      setBusy(false);
    }
  }

  async function requestBill() {
    if (!phone) {
      toast.show('Sign in to request your bill', 'error');
      return;
    }
    setBusy(true);
    try {
      await billService.request(phone, tableCode, total);
      toast.show('Bill requested — a waiter will confirm the amount', 'success');
      onChanged();
    } catch (err) {
      // The API explains why it refused; "try again" hid it and made a rejected table code
      // look like a flaky connection.
      toast.show(apiMessage(err, 'Could not request the bill, try again'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function payNow() {
    if (!bill || bill.status !== 'approved') return;
    setBusy(true);
    try {
      const order = await paymentService.createOrder(bill.id);
      if (!order.configured) {
        toast.show('Online payment isn’t set up yet — add the Cashfree keys, or pay at the counter.', 'info');
        setBusy(false);
        return;
      }
      const cashfree = await loadCashfree(order.mode);
      await cashfree.checkout({ paymentSessionId: order.paymentSessionId, redirectTarget: '_modal' });
      // Back from checkout → confirm with the server (the source of truth).
      const v = await paymentService.verify(bill.id, order.orderId);
      if (v.paid) {
        setPaid(true);
        onChanged();
      } else {
        toast.show(v.message || 'Payment not completed', 'error');
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Payment could not start', 'error');
    } finally {
      setBusy(false);
    }
  }

  function printBill() {
    const rows = items
      .map((i) => `<tr><td>${i.qty}× ${escapeHtml(i.name)}</td><td style="text-align:right">${format(i.qty * i.price)}</td></tr>`)
      .join('');
    const line = (l: string, v: string) => `<tr><td>${l}</td><td style="text-align:right">${v}</td></tr>`;
    const html = `<!doctype html><html><head><title>Bill</title><style>
      body{font-family:system-ui,sans-serif;padding:24px;color:#111}
      h2{margin:0 0 2px} .muted{color:#666;font-size:12px;margin-bottom:14px}
      table{width:100%;border-collapse:collapse;font-size:13px}
      td{padding:4px 0} .tot td{border-top:1px solid #ccc;font-weight:800;padding-top:8px;font-size:15px}
    </style></head><body>
      <h2>${escapeHtml(brand || 'Bill')}</h2><div class="muted">Bill · Table ${escapeHtml(tableCode)} · ${new Date().toLocaleString()}</div>
      <table>${rows}
        ${line('Sub total', format(subtotal))}
        ${
          // Rate-wise on the tax invoice too: a row per rate the bill actually carries, split into
          // CGST/SGST halves when the outlet charges intra-state GST. A tax invoice must show the split.
          buckets
            .map((b) =>
              hasSplitGst
                ? `${line(`CGST ${fmtRate(b.rate / 2)}%${b.inclusive ? ' (incl.)' : ''}`, format(Math.round(b.tax / 2)))}${line(`SGST ${fmtRate(b.rate / 2)}%${b.inclusive ? ' (incl.)' : ''}`, format(b.tax - Math.round(b.tax / 2)))}`
                : line(`GST ${fmtRate(b.rate)}%${b.inclusive ? ' (incl.)' : ''}`, format(b.tax))
            )
            .join('')
        }
        ${tax.service ? line(`Service ${tax.service}%`, format(serviceAmt)) : ''}
        ${discount ? line('Discount', '− ' + format(discount)) : ''}
        <tr class="tot"><td>Total paid</td><td style="text-align:right">${format(payable)}</td></tr>
      </table>
      <p class="muted" style="margin-top:16px">Thank you!</p>
      <script>window.onload=function(){window.print()}</script>
    </body></html>`;
    const w = window.open('', '_blank', 'width=380,height=640');
    if (w) {
      w.document.write(html);
      w.document.close();
    } else {
      toast.show('Allow pop-ups to print the bill', 'info');
    }
  }

  if (!open) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4' onClick={onClose}>
      <div
        className='flex max-h-[92vh] w-full max-w-[440px] flex-col overflow-hidden rounded-t-2xl bg-surface-card shadow-pop sm:rounded-2xl'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between border-b border-border px-5 py-3.5'>
          <div className='font-display text-[15px] font-bold'>{paid ? 'Payment successful' : 'Your bill'}</div>
          <button onClick={onClose} className='text-content-muted hover:text-content-primary' aria-label='Close'>
            <X size={18} />
          </button>
        </div>

        {paid ? (
          <div className='flex flex-col items-center gap-3 px-6 py-8 text-center'>
            <CheckCircle2 size={54} className='text-veg' />
            <div className='font-display text-lg font-extrabold'>Payment successful</div>
            <div className='text-[13px] text-content-secondary'>{format(payable)} paid via Cashfree · Table {tableCode}</div>
            <Button block size='lg' className='mt-2 gap-1.5' onClick={printBill}>
              <Printer size={16} /> Print / download bill
            </Button>
            <button onClick={onClose} className='mt-1 text-[12.5px] font-semibold text-content-muted'>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className='min-h-0 flex-1 overflow-y-auto px-5 py-4'>
              {/* Items */}
              <div className='mb-1 text-[11px] font-bold uppercase tracking-wider text-content-muted'>Items</div>
              {items.length === 0 && <div className='py-2 text-[12.5px] text-content-muted'>No items on this table yet.</div>}
              {items.map((i, n) => (
                <div key={n} className='flex items-center justify-between border-b border-border py-2 text-[13px]'>
                  <span>
                    <b className='font-display'>{i.qty}×</b> {i.name}
                  </span>
                  <span className='font-display font-semibold'>{format(i.qty * i.price)}</span>
                </div>
              ))}

              {/* Coupon */}
              <div className='mt-4 flex gap-2'>
                <div className='flex flex-1 items-center gap-2 rounded-lg border border-border px-3 py-2'>
                  <Tag size={14} className='text-content-muted' />
                  <input
                    value={code}
                    onChange={(e) => setCode(onlyCode(e.target.value.toUpperCase()).slice(0, 24))}
                    disabled={locked}
                    maxLength={24}
                    aria-label='Coupon code'
                    placeholder='Coupon code'
                    className='w-full bg-transparent text-[13px] font-semibold uppercase tracking-wide placeholder:font-normal placeholder:normal-case placeholder:text-content-muted focus:outline-none disabled:opacity-60'
                  />
                </div>
                <button
                  onClick={applyCoupon}
                  disabled={busy || locked || !code.trim()}
                  className='rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground disabled:opacity-50'
                >
                  Apply
                </button>
              </div>
              {couponMsg && (
                <div className={`mt-1.5 text-[11.5px] font-semibold ${discount > 0 ? 'text-veg' : 'text-danger'}`}>{couponMsg}</div>
              )}

              {/* Breakdown */}
              <div className='mt-4 rounded-xl bg-surface-card-hover p-3.5'>
                <Row label='Sub total' value={format(subtotal)} />
                {/* Inclusive prices already contain the GST, so nothing is added to the sub total —
                    a tax row here reads as an extra charge. Hidden on screen; the printed tax
                    invoice still carries the split, which GST law requires. */}
                {addsTax &&
                  shownBuckets.map((b) =>
                    hasSplitGst ? (
                      <Fragment key={b.rate}>
                        <Row label={`CGST @ ${fmtRate(b.rate / 2)}%`} value={format(Math.round(b.tax / 2))} />
                        <Row label={`SGST @ ${fmtRate(b.rate / 2)}%`} value={format(b.tax - Math.round(b.tax / 2))} />
                      </Fragment>
                    ) : (
                      <Row key={b.rate} label={`GST ${fmtRate(b.rate)}%`} value={format(b.tax)} />
                    )
                  )}
                {tax.service > 0 && <Row label={`Service @ ${tax.service}%`} value={format(serviceAmt)} />}
                {discount > 0 && <Row label='Coupon discount' value={`− ${format(discount)}`} good />}
                <div className='mt-1.5 flex items-center justify-between border-t border-border pt-2'>
                  <span className='font-display text-[14px] font-bold'>Total</span>
                  <span className='font-display text-[18px] font-extrabold text-primary'>{format(payable)}</span>
                </div>
              </div>
            </div>

            {/* Action — Request Bill, then (after approval) Pay Now. Pay is hidden until approved. */}
            <div className='border-t border-border p-4'>
              {!requested ? (
                <Button block size='lg' onClick={requestBill} disabled={busy || items.length === 0}>
                  {busy ? 'Requesting…' : 'Request Bill'}
                </Button>
              ) : approved ? (
                <Button block size='lg' onClick={payNow} disabled={busy}>
                  {busy ? 'Processing…' : `Pay Now · ${format(payable)}`}
                </Button>
              ) : (
                <div className='rounded-xl bg-warning-light px-4 py-3 text-center text-[12.5px] font-semibold text-warning'>
                  Bill requested — waiting for the counter to approve the amount
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, good }: { label: string; value: string; good?: boolean }) {
  return (
    <div className='flex justify-between py-0.5 text-[12.5px] text-content-secondary'>
      <span>{label}</span>
      <span className={good ? 'font-semibold text-veg' : ''}>{value}</span>
    </div>
  );
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));
}
